import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import queryString from 'query-string';

import { resetPassword } from 'data_system/token_auth/actions';
import {
  getResetPWError,
  getSubmitting,
  getBrand,
} from 'data_system/token_auth/selectors';

import {
  setHeaders,
  deleteHeaders,
  passwordValidationRules,
  isValidPassword,
} from 'data_system/token_auth/utils';

import PasswordValidator from 'ui_system/forms/PasswordValidator';

import { t } from 'services/translation';
import { titleize } from 'lib/string_utils';
import { Button } from './elements/Button';
import { FormFooter } from './FormFooter';
import {
  Label,
  Input,
  Heading,
  Form,
  FormContainer,
  Error,
  InputSection,
} from './styles_auth';
import { InvalidToken } from './InvalidToken';

import { RESET_PASSWORD_REQUEST } from './routes';

const ResetPasswordPage = () => {
  const dispatch = useDispatch();
  const { search } = useLocation();

  const queryParams = queryString.parse(search);
  const { invalid, uid = '', lang = 'en-US' } = queryParams;

  if (invalid) return <InvalidToken lang={lang} />;

  const email = uid.split('+')[0];

  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [passwordMatchError, setPasswordMatchError] = useState(null);

  const error = useSelector(getResetPWError);
  const submitting = useSelector(getSubmitting);
  const { name } = useSelector(getBrand);

  useEffect(() => {
    document.title = `${name} - Reset Password`;
  }, []);

  useEffect(() => {
    deleteHeaders();
  }, []);

  const passwordRules = passwordValidationRules(password);
  const isValid = isValidPassword(password, passwordRules);

  const handleSubmit = (e) => {
    e.preventDefault();

    // Prevent form submission and show error if passwords do not match
    if (password !== passwordConfirmation) {
      setPasswordMatchError(t('PASSWORDS_DO_NOT_MATCH'));
      return null;
    }

    setHeaders(queryParams);
    dispatch(resetPassword(password));
  };

  return (
    <FormContainer>
      <Heading bold>{t('RESET_PASSWORD')}</Heading>

      <Form onSubmit={handleSubmit}>
        <Label>{titleize(t('EMAIL_ADDRESS'))}</Label>
        <InputSection>
          <Input type="text" placeholder={email} disabled />
        </InputSection>
        <Label>{titleize(t('CHOOSE_NEW_PASSWORD'))}</Label>
        <InputSection>
          <Input
            onChange={(e) => setPassword(e.target.value)}
            type="password"
            value={password}
          />
        </InputSection>
        <PasswordValidator password={password} passwordRules={passwordRules} />
        <Label>{titleize(t('CONFIRM_NEW_PASSWORD'))}</Label>
        <InputSection>
          <Input
            onChange={(e) => setPasswordConfirmation(e.target.value)}
            type="password"
            value={passwordConfirmation}
          />
          {error ? <Error>{error}</Error> : null}
          {passwordMatchError ? <Error>{passwordMatchError}</Error> : null}
        </InputSection>
        <Button
          disabled={submitting}
          faded={!isValid}
          text={t('RESET_PASSWORD')}
        />
      </Form>

      <FormFooter />
    </FormContainer>
  );
};

export default ResetPasswordPage;
